import { z } from "zod";
import { classifyFetchError } from "../state/denialErrors";

const logoAssetPathSchema = z.string().trim().min(1).refine((value) => {
  if (!value.startsWith("/") || value.startsWith("//") || value.includes("\\")) return false;
  try {
    return new URL(value, "https://workspace.invalid").origin === "https://workspace.invalid";
  } catch {
    return false;
  }
}, "Logo asset URL must be a same-origin path");

/** Shared by company intelligence and compact client logos; never render a provider URL directly. */
export const companyLogoSchema = z.object({
  asset_url: logoAssetPathSchema,
  source_url: z.string().trim().url().refine(
    (value) => /^https?:\/\//i.test(value),
    "Source URL must use HTTP or HTTPS",
  ),
  retrieved_at: z.string().datetime({ offset: true }),
}).strict();

export const clientLogoMetadataSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("available"), logo: companyLogoSchema }).strict(),
  z.object({ status: z.literal("unavailable"), logo: z.null() }).strict(),
  z.object({ status: z.literal("pending"), logo: z.null() }).strict(),
]);

export type ClientLogoMetadata = z.infer<typeof clientLogoMetadataSchema>;

export async function fetchClientLogo(clientId: string): Promise<ClientLogoMetadata> {
  const params = new URLSearchParams({ reader_scope_node_id: clientId });
  const response = await fetch(`/api/workspace/outside-in/logo-metadata?${params}`, {
    credentials: "include",
    cache: "no-store",
  });
  if (!response.ok) throw await classifyFetchError(response, "client logo");
  return clientLogoMetadataSchema.parse(await response.json());
}
